import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { type Location, unstable_useBlocker as useBlocker } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { F0ActionBar, QuestionType } from '@factorialco/f0-react/dist/experimental'
import { FeatureFlagAvailabilitiesDocument, LoadFormDocument } from 'generated/resources/graphql'
import { isEqual } from 'lodash'

import Loading from 'modules/core/components/Loading'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { AgentStateType } from 'modules/forms/components/FormEditor'
import EnsureSaveDialog, { type EnsureSaveDialogHandle } from 'modules/forms/components/newFormEditor/EnsureSaveDialog'
import { FormWithAgent } from 'modules/forms/components/newFormEditor/formWithAgent'
import { FormWithoutAgent } from 'modules/forms/components/newFormEditor/formWithoutAgent'
import {
  FromFragmentToQuestionnaire,
  FromQuestionaireToMutationVariables,
} from 'modules/forms/components/newFormEditor/helpers'
import { useUpdateNewForm } from 'modules/forms/hooks/useForms'
import { useResourceHeader } from 'modules/layout/components/ResponsivePage/hooks'

import { QuestionnaireType } from './types'
import { useAutoOpenChat } from 'modules/ai/hooks/useAutoOpenChat'

const defaultState: QuestionnaireType = {
  title: '',
  description: '',
  elements: [],
}

interface Props {
  id?: string
  resourceType: string
  formType?: AgentStateType['formType']
  allowedQuestionTypes?: QuestionType[]
}

const NewFormEditor = ({ id, resourceType, formType, allowedQuestionTypes }: Props) => {
  const {
    company: { id: companyId },
  } = useCurrentSession()

  const { updateHeader, updateMetadataProperty } = useResourceHeader()

  const [form, setForm] = useState<QuestionnaireType>()
  const ensureSaveDialogRef = useRef<EnsureSaveDialogHandle>(null)

  const { updateForm } = useUpdateNewForm()

  const { data: featureFlagAvailabilities, loading: loadingFeatureFlags } = useQuery(
    FeatureFlagAvailabilitiesDocument,
    {
      variables: {
        companyId,
        featureNames: ['dev_ai_agent'],
      },
    }
  )
  // Todo: Move this query to this component
  const { data: formData, loading: loadingForm } = useQuery(LoadFormDocument, {
    variables: { id: id ?? '', resourceType },
    skip: !id,
  })

  const formIsDraft = formData?.forms?.form?.status === 'draft'

  const agentEnabled =
  (featureFlagAvailabilities?.features?.featuresConnection?.nodes.find(
    (feature) => feature.name === 'dev_ai_agent'
  )?.enabled ?? false) && formIsDraft

  useAutoOpenChat({
    shouldAutoOpen: !!agentEnabled,
  })

  const formTitle = form?.title
  const formDescription = form?.description

  useEffect(() => {
    if (formTitle && formDescription) {
      updateHeader((header) => ({
        ...(header || {}),
        title: formTitle,
        description: formDescription,
      }))
    }
  }, [formTitle, formDescription, updateHeader])

  const numberOfQuestions = form?.elements?.length || formData?.forms?.form?.elements?.length || 0

  useEffect(() => {
    if (loadingForm) return

    updateMetadataProperty(i18n.t('surveys.header.metadata.questions'), {
      type: 'text' as const,
      content: `${numberOfQuestions}`,
    })
  }, [loadingForm, numberOfQuestions])

  const initialState = useMemo(
    () => (formData?.forms?.form ? FromFragmentToQuestionnaire(formData.forms.form) : defaultState),
    [formData?.forms?.form]
  )

  const [reloadKey, setReloadKey] = useState(0)
  const [savedState, setSavedState] = useState<QuestionnaireType>(initialState)

  useEffect(() => {
    setSavedState(initialState)
  }, [initialState])

  const isDirty = useMemo(() => {
    if (!form) return false
    return !isEqual(form, savedState)
  }, [form, savedState])

  // Browser refresh/close tab blocking
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isDirty) {
        event.preventDefault()
        event.returnValue = ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isDirty])

  // React Router navigation blocking (including hash changes for activities)
  const shouldBlock = useCallback(
    ({ currentLocation, nextLocation }: { currentLocation: Location; nextLocation: Location }) => {
      const isPathChange = currentLocation.pathname !== nextLocation.pathname
      const isHashChange = currentLocation.hash !== nextLocation.hash
      return isDirty && (isPathChange || isHashChange) && formIsDraft && !currentLocation.hash
    },
    [isDirty, formIsDraft]
  )

  const blocker = useBlocker(shouldBlock)

  // Show modal when navigation is blocked
  useEffect(() => {
    if (blocker.state === 'blocked' && blocker.location) {
      ensureSaveDialogRef.current?.show(() => blocker.proceed())
    }
  }, [blocker])

  const showLoading = (loadingFeatureFlags && !featureFlagAvailabilities) || (loadingForm && !formData)

  if (showLoading) {
    return <Loading expand />
  }

  const onDiscard = () => {
    setReloadKey(reloadKey + 1)
  }

  const onSave = async () => {
    if (!formData?.forms.form || !form) return

    try {
      // Todo: Move this mutation to this component
      const result = await updateForm({
        ...FromQuestionaireToMutationVariables(
          formData.forms.form.id,
          formData.forms.form.anonymous,
          form
        ),
      })

      if (result.success) {
        setSavedState(form)
        setForm(undefined)

        app.setFlash({
          type: 'correct',
          message: i18n.t('trainings.surveys.create.edit_modal.form_success_message'),
          time: 1000,
        })
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('trainings.surveys.create.edit_modal.form_error_message'),
        })
      }
    } catch (error) {
      throw error
    }
  }

  const formCommonProps = {
    initialState,
    setForm,
    savedState,
    reloadKey,
    allowedQuestionTypes,
    enabled: formIsDraft,
  }

  const formComponent = agentEnabled ? (
    <FormWithAgent {...formCommonProps} formType={formType} />
  ) : (
    <FormWithoutAgent {...formCommonProps} />
  )

  return (
    <Box flexGrow height="full" padding="s16" paddingBottom="s96">
      {formComponent}
      {formIsDraft && (
        <>
          {isDirty && (
            <F0ActionBar
              isOpen
              primaryActions={[{ label: i18n.t('forms.action_bar.save'), onClick: onSave }]}
              secondaryActions={[{ label: i18n.t('forms.action_bar.discard'), onClick: onDiscard }]}
              label={i18n.t('forms.action_bar.label')}
            />
          )}
          <EnsureSaveDialog ref={ensureSaveDialogRef} onSave={onSave} />
        </>
      )}
    </Box>
  )
}

export default NewFormEditor
